import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SalesAnalytics from './SalesAnalytics';
import ConversionAnalytics from './ConversionAnalytics';
import EmailView from './EmailView';
import InvoicesView from './InvoicesView';
import ExpansionAnalytics from './ExpansionAnalytics';
import TutorialView from './TutorialView';
import SmartFollowUps from './SmartFollowUps';
import CommercialDashboard from './CommercialDashboard';
import CommercialAccounts from './CommercialAccounts';
import CommercialProspecting from './CommercialProspecting';
import BidInvitations from './BidInvitations';
import CommercialAnalytics from './CommercialAnalytics';
import { salesLogout, checkAuth } from '../../utils/salesAuth';
import { fmtDate, fmtFull, fmtDateTime, timeAgo as timeAgoUtil } from '../../utils/dateUtils';
import { useIsMobile } from '../../hooks/useIsMobile';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch — uses HttpOnly cookies
function getHeaders() { return { 'Content-Type': 'application/json' }; }
function authOpts(opts = {}) { return { ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } }; }

const STAGES = [
  { key: 'new', label: 'New', color: '#3b82f6' },
  { key: 'contact_attempted', label: 'Contact Attempted', color: '#6366f1' },
  { key: 'contacted', label: 'Contacted', color: '#8b5cf6' },
  { key: 'qualified', label: 'Qualified', color: '#f59e0b' },
  { key: 'site_visit', label: 'Site Visit', color: '#f97316' },
  { key: 'estimate_in_progress', label: 'Estimate In Progress', color: '#ef4444' },
  { key: 'estimate_sent', label: 'Estimate Sent', color: '#ec4899' },
  { key: 'follow_up', label: 'Follow Up', color: '#a855f7' },
  { key: 'customer_decision', label: 'Customer Decision', color: '#14b8a6' },
  { key: 'deposit_requested', label: 'Deposit Requested', color: '#22c55e' },
  { key: 'won', label: 'Won', color: '#16a34a' },
  { key: 'lost', label: 'Lost', color: '#6b7280' },
  { key: 'disqualified', label: 'Disqualified', color: '#9ca3af' },
];

function formatMoney(v) {
  if (!v) return '$0';
  return '$' + Math.round(v).toLocaleString();
}

function daysInStage(enteredAt) {
  if (!enteredAt) return 0;
  return Math.floor((Date.now() - new Date(enteredAt).getTime()) / 86400000);
}

const timeAgo = timeAgoUtil;

export default function SalesPortal() {
  const [view, setView] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [taskFilter, setTaskFilter] = useState('pending');
  const [dragOpp, setDragOpp] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    checkAuth().then(u => {
      if (!u) { navigate('/sales/login'); return; }
      setUser(u);
    });
  }, [navigate]);

  // Close sidebar when switching to desktop
  useEffect(() => { if (!isMobile) setSidebarOpen(false); }, [isMobile]);

  const fetchData = useCallback(async (endpoint) => {
    const res = await fetch(`${API}/api${endpoint}`, authOpts());
    if (res.status === 401) { salesLogout(navigate); return null; }
    return res.json();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    if (view === 'dashboard') fetchData('/sales/dashboard').then(d => d && setDashboard(d));
    if (view === 'pipeline') fetchData('/sales/pipeline').then(d => d && setPipeline(d));
    if (view === 'leads') fetchData('/sales/opportunities?limit=500').then(d => d && setLeads(d));
    if (view === 'requests') fetchData('/sales/quote-requests').then(d => d && setQuoteRequests(d));
    if (view === 'tasks') fetchData(`/sales/tasks?status=${taskFilter}`).then(d => d && setTasks(d));
    if (view === 'customers') fetchData('/sales/customers?limit=200').then(d => d && setCustomers(d));
  }, [view, user, taskFilter, fetchData]);

  const handleStageChange = async (oppId, newStage) => {
    await fetch(`${API}/api/sales/opportunities/${oppId}/stage`, authOpts({
      method: 'PUT', body: JSON.stringify({ stage: newStage }),
    }));
    fetchData('/sales/pipeline').then(d => d && setPipeline(d));
  };

  const handleDrop = (e, stageKey) => {
    e.preventDefault();
    if (dragOpp && dragOpp.current_stage !== stageKey) handleStageChange(dragOpp.id, stageKey);
    setDragOpp(null);
  };

  const completeTask = async (taskId) => {
    await fetch(`${API}/api/sales/tasks/${taskId}/complete`, authOpts({
      method: 'PUT', body: JSON.stringify({ result: 'Completed', next_action: '' }),
    }));
    fetchData(`/sales/tasks?status=${taskFilter}`).then(d => d && setTasks(d));
    if (view === 'dashboard') fetchData('/sales/dashboard').then(d => d && setDashboard(d));
  };

  const logout = () => salesLogout(navigate);

  const handleNavClick = (key) => {
    setView(key);
    if (isMobile) setSidebarOpen(false);
  };

  const [quoteRequests, setQuoteRequests] = useState([]);

  if (!user) return null;

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { key: 'pipeline', label: 'Pipeline', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
    { key: 'requests', label: 'Quote Requests', icon: 'M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20' },
    { key: 'email', label: 'Email', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { key: 'invoices', label: 'Invoices', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { key: 'leads', label: 'Leads', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
    { key: 'tasks', label: 'Tasks', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
    { key: 'customers', label: 'Customers', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { key: 'analytics', label: 'Analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { key: 'conversions', label: 'Conversions', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
    { key: 'expansion', label: 'Expansion', icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2' },
    { key: 'followups', label: 'Follow-Ups', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' },
    { key: 'divider-commercial', label: 'COMMERCIAL', isDivider: true },
    { key: 'commercial', label: 'Commercial', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { key: 'commercial-accounts', label: 'Accounts', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { key: 'prospecting', label: 'Prospecting', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' },
    { key: 'bids', label: 'Bid Invitations', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { key: 'comm-analytics', label: 'Analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { key: 'divider-tools', label: 'TOOLS', isDivider: true },
    { key: 'tutorial', label: 'Playbook', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
  ];

  const sidebarContent = (
    <>
      <div style={{ padding: '0 20px 20px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 2px', color: '#f1f5f9' }}>NTX Sales</h2>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>{user.name}</p>
        </div>
        {isMobile && (
          <button data-testid="close-sidebar" onClick={() => setSidebarOpen(false)}
            style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 22, cursor: 'pointer', padding: 4, lineHeight: 1 }}>
            &times;
          </button>
        )}
      </div>
      <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {navItems.map(item => {
          if (item.isDivider) {
            return (
              <div key={item.key} style={{ padding: '10px 20px 4px', fontSize: 9, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '1px' }}>
                {item.label}
              </div>
            );
          }
          return (
            <button key={item.key} data-testid={`nav-${item.key}`} onClick={() => handleNavClick(item.key)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', border: 'none',
                background: view === item.key ? '#334155' : 'transparent', color: view === item.key ? '#fff' : '#94a3b8',
                cursor: 'pointer', fontSize: 14, fontWeight: view === item.key ? 600 : 400, textAlign: 'left', transition: 'all .15s',
                minHeight: 44 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={item.icon}/></svg>
              {item.label}
            </button>
          );
        })}
      </nav>
      <div style={{ padding: '12px 20px', borderTop: '1px solid #334155' }}>
        <button data-testid="add-lead-btn" onClick={() => { navigate('/sales/leads/new'); if (isMobile) setSidebarOpen(false); }}
          style={{ width: '100%', padding: '9px 0', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 8, minHeight: 44 }}>
          + Add Lead
        </button>
        <button onClick={logout} style={{ width: '100%', padding: '8px 0', background: 'transparent', color: '#64748b', border: '1px solid #475569', borderRadius: 6, fontSize: 12, cursor: 'pointer', minHeight: 44 }}>
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div data-testid="sales-portal" style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif' }}>

      {/* Mobile Top Header */}
      {isMobile && (
        <div data-testid="mobile-header" style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 40, height: 52,
          background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 14px', boxShadow: '0 2px 8px rgba(0,0,0,.15)'
        }}>
          <button data-testid="hamburger-menu" onClick={() => setSidebarOpen(true)}
            style={{ background: 'none', border: 'none', color: '#f1f5f9', cursor: 'pointer', padding: 6, minHeight: 44, minWidth: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
          </button>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>NTX Sales</span>
          <button data-testid="mobile-add-lead" onClick={() => navigate('/sales/leads/new')}
            style={{ background: '#c0392b', border: 'none', color: '#fff', cursor: 'pointer', padding: '6px 12px', borderRadius: 5, fontSize: 12, fontWeight: 600, minHeight: 36 }}>
            + Lead
          </button>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <div data-testid="sidebar-backdrop" onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 49 }} />
      )}

      {/* Sidebar */}
      <aside style={{
        width: isMobile ? 280 : 220,
        background: '#1e293b', color: '#fff', padding: '20px 0', flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        ...(isMobile ? {
          position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 50,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease-out',
          overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        } : {}),
      }}>
        {sidebarContent}
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, overflow: 'auto', ...(isMobile ? { paddingTop: 52 } : {}) }}>
        {view === 'dashboard' && <DashboardView data={dashboard} onCompleteTask={completeTask} navigate={navigate} isMobile={isMobile} />}
        {view === 'pipeline' && <PipelineView pipeline={pipeline} onDragStart={setDragOpp} onDrop={handleDrop} dragOpp={dragOpp} navigate={navigate} isMobile={isMobile} />}
        {view === 'leads' && <LeadsView leads={leads} search={searchTerm} onSearch={setSearchTerm} navigate={navigate} isMobile={isMobile} />}
        {view === 'requests' && <QuoteRequestsView requests={quoteRequests} setRequests={setQuoteRequests} navigate={navigate} isMobile={isMobile} />}
        {view === 'email' && <EmailView />}
        {view === 'invoices' && <InvoicesView />}
        {view === 'tasks' && <TasksView tasks={tasks} filter={taskFilter} onFilter={setTaskFilter} onComplete={completeTask} navigate={navigate} isMobile={isMobile} />}
        {view === 'customers' && <CustomersView customers={customers} navigate={navigate} isMobile={isMobile} />}
        {view === 'analytics' && <SalesAnalytics />}
        {view === 'conversions' && <ConversionAnalytics />}
        {view === 'expansion' && <ExpansionAnalytics navigate={navigate} />}
        {view === 'followups' && <SmartFollowUps navigate={navigate} />}
        {view === 'commercial' && <CommercialDashboard navigate={navigate} />}
        {view === 'commercial-accounts' && <CommercialAccounts navigate={navigate} />}
        {view === 'prospecting' && <CommercialProspecting navigate={navigate} />}
        {view === 'bids' && <BidInvitations navigate={navigate} />}
        {view === 'comm-analytics' && <CommercialAnalytics navigate={navigate} />}
        {view === 'tutorial' && <TutorialView />}
      </main>
    </div>
  );
}

/* ═══════════════ DASHBOARD VIEW ═══════════════ */
function DashboardView({ data, onCompleteTask, navigate, isMobile }) {
  const [priorities, setPriorities] = useState(null);
  const [atRisk, setAtRisk] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/sales/todays-priorities`, authOpts()).then(r => r.ok ? r.json() : null).then(d => d && setPriorities(d));
    fetch(`${API}/api/sales/revenue-at-risk`, authOpts()).then(r => r.ok ? r.json() : null).then(d => d && setAtRisk(d));
  }, []);

  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>;
  const metrics = [
    { label: 'Total Customers', value: data.total_customers, color: '#3b82f6' },
    { label: 'Open Opportunities', value: data.open_opportunities, color: '#f59e0b' },
    { label: 'Pipeline Value', value: formatMoney(data.pipeline_value), color: '#8b5cf6' },
    { label: 'Won', value: data.won, color: '#16a34a' },
    { label: 'Won Value', value: formatMoney(data.won_value), color: '#22c55e' },
    { label: 'Revenue at Risk', value: atRisk ? formatMoney(atRisk.total_at_risk_value) : '...', color: atRisk?.total_at_risk_value > 0 ? '#dc2626' : '#6b7280' },
  ];

  const CATEGORY_STYLES = {
    new_lead: { bg: '#eef2ff', border: '#c7d2fe', icon: '🆕', color: '#4338ca' },
    overdue_task: { bg: '#fef2f2', border: '#fecaca', icon: '⏰', color: '#dc2626' },
    today_task: { bg: '#f0fdf4', border: '#bbf7d0', icon: '📋', color: '#16a34a' },
    stalled_estimate: { bg: '#fffbeb', border: '#fde68a', icon: '📊', color: '#d97706' },
  };

  return (
    <div data-testid="dashboard-view" style={{ padding: isMobile ? '16px 12px' : '24px 28px' }}>
      <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>Dashboard</h1>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(170px, 1fr))', gap: isMobile ? 8 : 14, marginBottom: isMobile ? 16 : 28 }}>
        {metrics.map((m, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '12px 12px' : '16px 18px', borderLeft: `4px solid ${m.color}`, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <div style={{ fontSize: isMobile ? 10 : 12, color: '#64748b', fontWeight: 500 }}>{m.label}</div>
            <div style={{ fontSize: isMobile ? 17 : 22, fontWeight: 700, color: '#1e293b', marginTop: 2 }}>{m.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 12 : 20, marginBottom: isMobile ? 12 : 20 }}>
        {/* Today's Priorities */}
        <div data-testid="todays-priorities" style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '14px 12px' : '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 22, height: 22, background: '#4338ca', borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 800 }}>AI</span>
            Today's Priorities {priorities ? `(${priorities.total})` : ''}
          </h3>
          {!priorities ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading...</p> :
            priorities.priorities.length === 0 ? <p style={{ color: '#94a3b8', fontSize: 13 }}>No priorities for today</p> :
            <div style={{ maxHeight: isMobile ? 340 : 420, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingRight: 4 }}>
            {priorities.priorities.slice(0, 20).map((p, i) => {
              const style = CATEGORY_STYLES[p.category] || CATEGORY_STYLES.today_task;
              return (
                <div key={i} onClick={() => p.opportunity && navigate(`/sales/opportunity/${p.opportunity.id}`)}
                  style={{ padding: '10px 12px', marginBottom: 6, borderRadius: 8, border: `1px solid ${style.border}`, background: style.bg, cursor: p.opportunity ? 'pointer' : 'default', transition: 'transform .1s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>{style.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{p.subtitle}</div>
                    </div>
                    {p.opportunity?.estimated_value > 0 && (
                      <span style={{ fontSize: 12, fontWeight: 700, color: style.color }}>{formatMoney(p.opportunity.estimated_value)}</span>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          }
        </div>

        {/* Revenue at Risk */}
        <div data-testid="revenue-at-risk" style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '14px 12px' : '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Revenue at Risk</h3>
          {atRisk?.total_at_risk_value > 0 && (
            <div data-testid="at-risk-value" style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: '#dc2626', marginBottom: 12 }}>{formatMoney(atRisk.total_at_risk_value)}</div>
          )}
          <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 12px' }}>Open quoted opportunities requiring attention</p>
          {!atRisk ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading...</p> :
            atRisk.count === 0 ? <p style={{ color: '#16a34a', fontSize: 13, fontWeight: 600 }}>No opportunities at risk</p> :
            atRisk.opportunities.slice(0, 6).map((item, i) => (
              <div key={i} onClick={() => navigate(`/sales/opportunity/${item.opportunity.id}`)}
                style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.opportunity.customer?.name || 'Unknown'} — {formatMoney(item.value)}
                    </div>
                    <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>
                      {item.risk_reasons.slice(0, 2).join(' • ')}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: item.risk_score >= 50 ? '#fef2f2' : '#fefce8', color: item.risk_score >= 50 ? '#dc2626' : '#d97706', fontWeight: 600, flexShrink: 0 }}>
                    {item.risk_score}
                  </span>
                </div>
              </div>
            ))
          }
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 12 : 20 }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '14px 12px' : '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', margin: '0 0 14px' }}>Today's Tasks ({(data.today_tasks || []).length})</h3>
          {(data.today_tasks || []).length === 0 && <p style={{ color: '#94a3b8', fontSize: 13 }}>No tasks due today</p>}
          {(data.today_tasks || []).map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{t.task_type} — {t.priority}</div>
              </div>
              <button onClick={() => onCompleteTask(t.id)} style={{ padding: '6px 12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', flexShrink: 0, minHeight: 36 }}>Done</button>
            </div>
          ))}
        </div>
        <div style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '14px 12px' : '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', margin: '0 0 14px' }}>Recent Activity</h3>
          {(data.recent_activities || []).length === 0 && <p style={{ color: '#94a3b8', fontSize: 13 }}>No recent activity</p>}
          {(data.recent_activities || []).slice(0, 8).map(a => (
            <div key={a.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 13, color: '#334155' }}>{a.description}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{timeAgo(a.created_at)} — {a.type}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ PIPELINE / KANBAN VIEW ═══════════════ */
function PipelineView({ pipeline, onDragStart, onDrop, dragOpp, navigate, isMobile }) {
  if (!pipeline) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading pipeline...</div>;
  return (
    <div data-testid="pipeline-view" style={{ padding: isMobile ? '12px 8px' : '24px 16px' }}>
      <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b', margin: '0 0 16px', paddingLeft: 12 }}>Sales Pipeline</h1>
      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 20, minHeight: isMobile ? 'auto' : 'calc(100vh - 120px)', WebkitOverflowScrolling: 'touch' }}>
        {STAGES.map(stage => {
          const stageData = pipeline[stage.key] || { count: 0, value: 0, opportunities: [] };
          return (
            <div key={stage.key} data-testid={`stage-${stage.key}`}
              onDragOver={e => e.preventDefault()}
              onDrop={e => onDrop(e, stage.key)}
              style={{ minWidth: isMobile ? 200 : 240, maxWidth: isMobile ? 220 : 280, flex: '0 0 auto', background: '#fff', borderRadius: 10,
                display: 'flex', flexDirection: 'column', border: dragOpp ? '2px dashed #cbd5e1' : '1px solid #e2e8f0' }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', background: `${stage.color}10`, borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: isMobile ? 10 : 12, fontWeight: 700, color: stage.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{stage.label}</span>
                  <span style={{ fontSize: 11, background: stage.color, color: '#fff', borderRadius: 10, padding: '1px 7px', fontWeight: 600 }}>{stageData.count}</span>
                </div>
                {stageData.value > 0 && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{formatMoney(stageData.value)}</div>}
              </div>
              <div style={{ flex: 1, padding: 8, overflowY: 'auto', maxHeight: isMobile ? 300 : 'calc(100vh - 220px)' }}>
                {stageData.opportunities.map(opp => (
                  <div key={opp.id} data-testid={`opp-card-${opp.id}`}
                    draggable={!isMobile}
                    onDragStart={() => !isMobile && onDragStart(opp)}
                    onClick={() => navigate(`/sales/opportunity/${opp.id}`)}
                    style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 6, cursor: 'pointer',
                      border: '1px solid #e2e8f0', transition: 'box-shadow .15s' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.customer?.name || 'Unknown'}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.title}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: stage.color }}>{formatMoney(opp.estimated_value || opp.quoted_value)}</span>
                      <span style={{ fontSize: 10, color: '#94a3b8', background: '#f1f5f9', borderRadius: 4, padding: '1px 5px' }}>
                        {daysInStage(opp.stage_entered_at)}d
                      </span>
                    </div>
                    {opp.priority === 'high' && <span style={{ fontSize: 10, background: '#fef2f2', color: '#dc2626', borderRadius: 3, padding: '1px 5px', marginTop: 4, display: 'inline-block' }}>HIGH</span>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════ LEADS VIEW ═══════════════ */
function LeadsView({ leads, search, onSearch, navigate, isMobile }) {
  const filtered = leads.filter(l => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.customer?.name || '').toLowerCase().includes(s) || (l.title || '').toLowerCase().includes(s) || (l.customer?.phone || '').includes(s) || (l.customer?.email || '').toLowerCase().includes(s);
  });
  return (
    <div data-testid="leads-view" style={{ padding: isMobile ? '16px 12px' : '24px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 16, flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
        <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>All Leads ({filtered.length})</h1>
        <input data-testid="leads-search" value={search} onChange={e => onSearch(e.target.value)} placeholder="Search name, phone, email..."
          style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: isMobile ? '100%' : 260, outline: 'none', boxSizing: 'border-box' }} />
      </div>
      {isMobile ? (
        /* Mobile: Card Layout */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.slice(0, 50).map(opp => {
            const stg = STAGES.find(s => s.key === opp.current_stage) || { label: opp.current_stage, color: '#6b7280' };
            return (
              <div key={opp.id} onClick={() => navigate(`/sales/opportunity/${opp.id}`)}
                style={{ background: '#fff', borderRadius: 10, padding: '14px 14px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', cursor: 'pointer', borderLeft: `4px solid ${stg.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 14, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.customer?.name || '—'}</div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', flexShrink: 0 }}>{formatMoney(opp.estimated_value || opp.quoted_value)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#475569', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.title}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ background: `${stg.color}15`, color: stg.color, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{stg.label}</span>
                  {opp.customer?.phone && <a href={`tel:${opp.customer.phone}`} onClick={e => e.stopPropagation()} style={{ fontSize: 11, color: '#c0392b', fontWeight: 500 }}>{opp.customer.phone}</a>}
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>{timeAgo(opp.last_activity_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Desktop: Table Layout */
        <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  {['Customer', 'Project', 'Stage', 'Value', 'Source', 'Last Activity', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 100).map(opp => {
                  const stg = STAGES.find(s => s.key === opp.current_stage) || { label: opp.current_stage, color: '#6b7280' };
                  return (
                    <tr key={opp.id} onClick={() => navigate(`/sales/opportunity/${opp.id}`)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                      onMouseOver={e => e.currentTarget.style.background = '#f8fafc'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{opp.customer?.name || '—'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{opp.customer?.phone || ''} {opp.customer?.email ? `• ${opp.customer.email}` : ''}</div>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#475569' }}>{opp.title}</td>
                      <td style={{ padding: '10px 14px' }}><span style={{ background: `${stg.color}15`, color: stg.color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{stg.label}</span></td>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e293b' }}>{formatMoney(opp.estimated_value || opp.quoted_value)}</td>
                      <td style={{ padding: '10px 14px', color: '#64748b', fontSize: 12 }}>{(opp.lead_source || '').replace(/_/g, ' ')}</td>
                      <td style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 12 }}>{timeAgo(opp.last_activity_at)}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M9 5l7 7-7 7"/></svg>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════ QUOTE REQUESTS VIEW ═══════════════ */
function QuoteRequestsView({ requests, setRequests, navigate, isMobile }) {
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [converting, setConverting] = useState(null);

  const updateStatus = async (id, status) => {
    const res = await fetch(`${API}/api/sales/quote-requests/${id}/status`, authOpts({
      method: 'PATCH', body: JSON.stringify({ status }),
    }));
    if (res.ok) setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
  };

  const convertToOpportunity = async (id) => {
    setConverting(id);
    try {
      const res = await fetch(`${API}/api/sales/quote-requests/${id}/convert`, authOpts({
        method: 'POST',
      }));
      if (res.ok) {
        const data = await res.json();
        setRequests(prev => prev.map(r => r.id === id ? {
          ...r, status: 'converted', opportunity_id: data.opportunity.id,
          opportunity: data.opportunity, customer_id: data.customer.id,
        } : r));
        navigate(`/sales/opportunity/${data.opportunity.id}`);
      }
    } finally { setConverting(null); }
  };

  const filtered = requests.filter(r => {
    if (filter !== 'all' && (r.status || 'pending') !== filter) return false;
    if (sourceFilter !== 'all' && r.source_type !== sourceFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(s) || (r.phone || '').includes(s) || (r.email || '').toLowerCase().includes(s) || (r.message || '').toLowerCase().includes(s);
  });

  const statusColors = { pending: { bg: '#fef9c3', text: '#ca8a04' }, contacted: { bg: '#dbeafe', text: '#2563eb' }, converted: { bg: '#dcfce7', text: '#16a34a' }, closed: { bg: '#f1f5f9', text: '#64748b' } };
  const sourceLabels = { quote_request: { label: 'Quote Request', bg: '#ede9fe', text: '#7c3aed' }, instant_estimate: { label: 'Instant Estimate', bg: '#fce7f3', text: '#db2777' } };

  const qrCount = requests.filter(r => r.source_type === 'quote_request').length;
  const ieCount = requests.filter(r => r.source_type === 'instant_estimate').length;

  return (
    <div data-testid="quote-requests-view" style={{ padding: isMobile ? '16px 12px' : '24px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 16, flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Incoming Requests ({filtered.length})</h1>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{qrCount} quote requests + {ieCount} instant estimates from the website</p>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {['all', 'quote_request', 'instant_estimate'].map(f => (
            <button key={f} onClick={() => setSourceFilter(f)} data-testid={`qr-source-${f}`}
              style={{ padding: '5px 10px', borderRadius: 6, border: sourceFilter === f ? 'none' : '1px solid #d1d5db', background: sourceFilter === f ? '#1e293b' : '#fff', color: sourceFilter === f ? '#fff' : '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer', minHeight: 36 }}>
              {f === 'all' ? 'All' : f === 'quote_request' ? 'Quote Req' : 'Estimates'}
            </button>
          ))}
          <span style={{ color: '#d1d5db' }}>|</span>
          {['all', 'pending', 'contacted', 'converted', 'closed'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`qr-filter-${f}`}
              style={{ padding: '5px 10px', borderRadius: 6, border: filter === f ? 'none' : '1px solid #d1d5db', background: filter === f ? '#1e293b' : '#fff', color: filter === f ? '#fff' : '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', minHeight: 36 }}>
              {f}
            </button>
          ))}
          <input data-testid="qr-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: isMobile ? '100%' : 160, outline: 'none' }} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>No requests found.</p>}
        {filtered.map(r => {
          const sc = statusColors[r.status] || statusColors.pending;
          const sl = sourceLabels[r.source_type] || sourceLabels.quote_request;
          const date = r.submitted_at ? true : false;
          const pricing = r.pricing_result || r.project_data?.pricing_result;
          return (
            <div key={r.id} data-testid={`qr-card-${r.id}`}
              style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', borderLeft: `4px solid ${sc.text}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{r.name || 'No Name'}</span>
                    <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: sl.bg, color: sl.text }}>{sl.label}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.text, textTransform: 'uppercase' }}>{r.status || 'pending'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748b', marginBottom: 6, flexWrap: 'wrap' }}>
                    {r.phone && <span><strong>Phone:</strong> <a href={`tel:${r.phone}`} style={{ color: '#2563eb' }}>{r.phone}</a></span>}
                    {r.email && <span><strong>Email:</strong> <a href={`mailto:${r.email}`} style={{ color: '#2563eb' }}>{r.email}</a></span>}
                    {r.project_type && <span><strong>Type:</strong> {r.project_type}</span>}
                    {r.source_type === 'instant_estimate' && r.services && <span><strong>Services:</strong> {r.services.join(', ')}</span>}
                  </div>
                  {r.message && <p style={{ fontSize: 13, color: '#475569', margin: '6px 0 0', lineHeight: 1.5, background: '#f8fafc', padding: '8px 12px', borderRadius: 6 }}>{r.message}</p>}
                  {pricing && (
                    <div style={{ marginTop: 6, padding: '6px 10px', background: '#fff7ed', borderRadius: 4, fontSize: 12, color: '#c2410c', display: 'flex', gap: 16 }}>
                      {pricing.total_estimate && <span><strong>Estimate:</strong> {formatMoney(pricing.total_estimate)}</span>}
                      {pricing.customer_range && <span><strong>Range:</strong> {formatMoney(pricing.customer_range.low)} – {formatMoney(pricing.customer_range.high)}</span>}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                  {date && <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtFull(r.submitted_at)}</span>}
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {(r.status || 'pending') !== 'contacted' && (
                      <button data-testid={`qr-mark-contacted-${r.id}`} onClick={() => updateStatus(r.id, 'contacted')}
                        style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: '#dbeafe', color: '#2563eb', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        Mark Contacted
                      </button>
                    )}
                    {r.opportunity_id && (
                      <button data-testid={`qr-view-opp-${r.id}`} onClick={() => navigate(`/sales/opportunity/${r.opportunity_id}`)}
                        style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: '#dcfce7', color: '#16a34a', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        View Opportunity
                      </button>
                    )}
                    {!r.opportunity_id && (r.status || 'pending') !== 'closed' && (
                      <button data-testid={`qr-convert-${r.id}`} onClick={() => convertToOpportunity(r.id)}
                        disabled={converting === r.id}
                        style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: '#c0392b', color: '#fff', fontSize: 11, fontWeight: 600, cursor: converting === r.id ? 'wait' : 'pointer', opacity: converting === r.id ? 0.7 : 1 }}>
                        {converting === r.id ? 'Converting...' : 'Convert to Opp'}
                      </button>
                    )}
                    {(r.status || 'pending') !== 'closed' && !r.opportunity_id && (
                      <button data-testid={`qr-close-${r.id}`} onClick={() => updateStatus(r.id, 'closed')}
                        style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: '#f1f5f9', color: '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        Close
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {r.opportunity && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: '#f0fdf4', borderRadius: 4, fontSize: 11, color: '#16a34a', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                  onClick={() => navigate(`/sales/opportunity/${r.opportunity.id}`)}>
                  <span>Linked Opportunity: <strong>{r.opportunity.title}</strong></span>
                  <span style={{ textTransform: 'capitalize' }}>{(r.opportunity.current_stage || '').replace(/_/g, ' ')} {r.opportunity.estimated_value ? `• ${formatMoney(r.opportunity.estimated_value)}` : ''}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════ TASKS VIEW ═══════════════ */
function TasksView({ tasks, filter, onFilter, onComplete, navigate, isMobile }) {
  const now = new Date().toISOString();
  return (
    <div data-testid="tasks-view" style={{ padding: isMobile ? '16px 12px' : '24px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 16, flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
        <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>Tasks ({tasks.length})</h1>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['pending', 'completed', 'cancelled'].map(f => (
            <button key={f} data-testid={`task-filter-${f}`} onClick={() => onFilter(f)}
              style={{ padding: '6px 14px', borderRadius: 6, border: filter === f ? 'none' : '1px solid #d1d5db',
                background: filter === f ? '#1e293b' : '#fff', color: filter === f ? '#fff' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 36 }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
        {tasks.length === 0 && <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No {filter} tasks</div>}
        {tasks.map(t => {
          const overdue = t.status === 'pending' && t.due_at && t.due_at < now;
          return (
            <div key={t.id} data-testid={`task-${t.id}`}
              style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', padding: isMobile ? '12px 12px' : '14px 18px', borderBottom: '1px solid #f1f5f9',
                background: overdue ? '#fef2f2' : 'transparent', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 8 : 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>{t.description}</div>
                <div style={{ fontSize: 12, color: overdue ? '#dc2626' : '#94a3b8', marginTop: 2 }}>
                  {t.task_type} — {t.priority}
                  {t.due_at && ` — Due: ${fmtDate(t.due_at)}`}
                  {overdue && ' — OVERDUE'}
                  {t.automation_generated && ' — Auto-generated'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                {t.opportunity_id && (
                  <button onClick={() => navigate(`/sales/opportunity/${t.opportunity_id}`)}
                    style={{ padding: '6px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11, cursor: 'pointer', color: '#475569', minHeight: 36 }}>View</button>
                )}
                {t.status === 'pending' && (
                  <button onClick={() => onComplete(t.id)}
                    style={{ padding: '6px 12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', minHeight: 36 }}>Complete</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════ CUSTOMERS VIEW ═══════════════ */
function CustomersView({ customers, navigate, isMobile }) {
  const [search, setSearch] = useState('');
  const filtered = customers.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s) || (c.phone || '').includes(s);
  });
  return (
    <div data-testid="customers-view" style={{ padding: isMobile ? '16px 12px' : '24px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 16, flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
        <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>Customers ({filtered.length})</h1>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customers..."
          style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: isMobile ? '100%' : 240, outline: 'none', boxSizing: 'border-box' }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
        {filtered.slice(0, 50).map(c => (
          <div key={c.id} onClick={() => navigate(`/sales/customer/${c.id}`)}
            style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'border-color .15s' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>{c.name || '—'}</div>
            {c.company && <div style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>{c.company}</div>}
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {c.phone && <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()} style={{ color: '#c0392b' }}>{c.phone}</a>}
              {c.email && <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} style={{ color: '#2563eb' }}>{c.email}</a>}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{(c.source || 'manual').replace(/_/g, ' ')} — {c.created_at ? fmtDate(c.created_at) : ''}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
